import { ValidationError } from "../errors.ts";
import type { NormalizedShareSpec, ResolvedShare, ShareSpec } from "./types.ts";

export function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

/** What the customer is invoiced for a share: cost plus margin, in cents. */
export function rebillAmount(amount: number, marginPct: number): number {
  return roundCents(amount * (1 + marginPct / 100));
}

function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/**
 * Validates share specs without knowing the expense amount (rules have none):
 * every line needs a client, fixed amounts must be > 0, margins ≥ 0, and at
 * most one line may be Rest. Text fields are trimmed; empty becomes null.
 */
export function normalizeShareSpecs(specs: ShareSpec[], defaultMarginPct: number): NormalizedShareSpec[] {
  if (!Array.isArray(specs) || specs.length === 0) throw new ValidationError("at least one share is required");
  let rest = 0;
  const out = specs.map((s) => {
    if (!isNum(s.clientId)) throw new ValidationError("share clientId is required");
    const isRest = s.amount === undefined || s.amount === null;
    if (isRest) rest++;
    else if (!isNum(s.amount)) throw new ValidationError("share amount must be > 0");
    const amount = isRest ? null : roundCents(s.amount as number);
    if (amount !== null && !(amount > 0)) throw new ValidationError("share amount must be > 0");
    const marginPct = s.marginPct ?? defaultMarginPct;
    if (!isNum(marginPct) || marginPct < 0) throw new ValidationError("share margin must be >= 0");
    return {
      clientId: s.clientId,
      amount,
      marginPct,
      rebillRef: s.rebillRef?.trim() || null,
      note: s.note?.trim() || null,
    };
  });
  if (rest > 1) throw new ValidationError("only one Rest share is allowed");
  return out;
}

/** Normalizes, checks the fixed lines fit into `total`, and gives the Rest line what is left. */
export function resolveShares(total: number, specs: ShareSpec[], defaultMarginPct: number): ResolvedShare[] {
  const normalized = normalizeShareSpecs(specs, defaultMarginPct);
  const fixed = roundCents(normalized.reduce((sum, s) => sum + (s.amount ?? 0), 0));
  if (fixed > roundCents(total) + 0.005) throw new ValidationError(`shares (${fixed}) exceed the expense amount (${total})`);
  const rest = roundCents(total - fixed);
  return normalized.map((s) => {
    if (s.amount !== null) return { ...s, amount: s.amount };
    if (rest <= 0) throw new ValidationError("nothing left for the Rest share");
    return { ...s, amount: rest };
  });
}
